import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import styled from 'styled-components';
import {
  findTourTarget,
  scrollTourTargetIntoView,
  waitForTourTarget,
} from './anchors';
import {
  TIP_PAD,
  TIP_STICKY,
  clampSpotlight,
  placeTipCard,
  type CardPos,
  type Rect,
} from './geometry';
import type { TipStep, TipTokenPrefix } from './types';

export { clampSpotlight, placeTipCard } from './geometry';

type CoachmarkProps = {
  tokenPrefix: TipTokenPrefix;
  step: TipStep | null;
  stepIndex: number;
  stepCount: number;
  onNext: () => void;
  onBack?: () => void;
  onSkip: () => void;
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 80;
  pointer-events: none;
  overflow: hidden;
`;

/** Invisible hit targets around the hole — visual dim comes from Spot. */
const Dim = styled.button`
  position: absolute;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: default;
  pointer-events: auto;
`;

/**
 * Rounded cutout matching the tip card radius. The spread shadow paints the
 * dimmed scrim so the hole itself has the same soft corners as the tip box.
 */
const Spot = styled.div<{ $prefix: TipTokenPrefix }>`
  position: absolute;
  box-sizing: border-box;
  border-radius: var(--${({ $prefix }) => $prefix}-r-card);
  box-shadow: 0 0 0 9999px rgba(16, 21, 28, 0.48);
  pointer-events: none;
`;

const Card = styled(motion.div)<{ $prefix: TipTokenPrefix }>`
  position: fixed;
  z-index: 81;
  width: min(360px, calc(100vw - 32px));
  max-width: calc(100vw - 32px);
  background: var(--${({ $prefix }) => $prefix}-paper);
  color: var(--${({ $prefix }) => $prefix}-ink);
  border: 1px solid var(--${({ $prefix }) => $prefix}-hairline);
  border-radius: var(--${({ $prefix }) => $prefix}-r-card);
  box-shadow: var(--${({ $prefix }) => $prefix}-shadow-overlay);
  padding: 18px 18px 14px;
  font-family: var(--${({ $prefix }) => $prefix}-font-ui);
  pointer-events: auto;
  box-sizing: border-box;
`;

const Eyebrow = styled.p<{ $prefix: TipTokenPrefix }>`
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--${({ $prefix }) => $prefix}-muted);
`;

const Title = styled.h2<{ $prefix: TipTokenPrefix }>`
  margin: 0 0 8px;
  font-family: var(--${({ $prefix }) => $prefix}-font-display);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--${({ $prefix }) => $prefix}-ink);
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const Body = styled.p<{ $prefix: TipTokenPrefix }>`
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--${({ $prefix }) => $prefix}-muted);
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const StepMeta = styled.span<{ $prefix: TipTokenPrefix }>`
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--${({ $prefix }) => $prefix}-muted);
  flex: 0 0 auto;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
  flex-wrap: wrap;
`;

const GhostBtn = styled.button<{ $prefix: TipTokenPrefix }>`
  appearance: none;
  border: none;
  background: transparent;
  color: var(--${({ $prefix }) => $prefix}-muted);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 8px;
  white-space: nowrap;

  &:hover {
    color: var(--${({ $prefix }) => $prefix}-ink);
  }
`;

const PrimaryBtn = styled.button<{ $prefix: TipTokenPrefix }>`
  appearance: none;
  border: 1px solid var(--${({ $prefix }) => $prefix}-navy);
  background: var(--${({ $prefix }) => $prefix}-navy);
  color: #fff;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 7px 12px;
  border-radius: var(--${({ $prefix }) => $prefix}-r-input);
  white-space: nowrap;

  &:hover {
    background: var(--${({ $prefix }) => $prefix}-navy-deep, #041a4d);
  }
`;

const measureRaw = (el: HTMLElement): Rect => {
  const r = el.getBoundingClientRect();
  return {
    top: r.top,
    left: r.left,
    width: Math.max(0, r.width),
    height: Math.max(0, r.height),
  };
};

export const Coachmark = ({
  tokenPrefix,
  step,
  stepIndex,
  stepCount,
  onNext,
  onBack,
  onSkip,
}: CoachmarkProps) => {
  const reduceMotion = useReducedMotion();
  const [spot, setSpot] = useState<Rect | null>(null);
  const [cardPos, setCardPos] = useState<CardPos | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const onNextRef = useRef(onNext);
  const onSkipRef = useRef(onSkip);
  const skippedMissingRef = useRef<string | null>(null);
  onNextRef.current = onNext;
  onSkipRef.current = onSkip;

  useEffect(() => {
    if (!step) {
      setSpot(null);
      setCardPos(null);
      return undefined;
    }

    let cancelled = false;
    setSpot(null);
    setCardPos(null);
    const stepKey = step.id;

    (async () => {
      const el = await waitForTourTarget(step.target);
      if (cancelled) return;
      if (!el) {
        if (skippedMissingRef.current !== stepKey) {
          skippedMissingRef.current = stepKey;
          onNextRef.current();
        }
        return;
      }
      skippedMissingRef.current = null;
      await scrollTourTargetIntoView(el, {
        reduceMotion: Boolean(reduceMotion),
      });
      if (cancelled) return;
      setSpot(clampSpotlight(measureRaw(el)));
    })();

    return () => {
      cancelled = true;
    };
  }, [step, reduceMotion]);

  useLayoutEffect(() => {
    if (!step || !spot) return undefined;
    const update = () => {
      const el = findTourTarget(step.target);
      if (!el) return;
      setSpot(clampSpotlight(measureRaw(el)));
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [step, spot]);

  useLayoutEffect(() => {
    if (!step || !spot) {
      setCardPos(null);
      return;
    }
    const node = cardRef.current;
    const cardW = Math.min(360, window.innerWidth - 32);
    const cardH = node?.offsetHeight || 200;
    setCardPos(placeTipCard(spot, cardW, cardH, step.placement));
  }, [step, spot]);

  useEffect(() => {
    if (!step) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSkipRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step]);

  const isLast = stepIndex >= stepCount - 1;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;

  return (
    <AnimatePresence>
      {step && spot && (
        <Overlay role="dialog" aria-modal="true" aria-label={step.title}>
          <Spot
            $prefix={tokenPrefix}
            aria-hidden
            style={{
              top: spot.top,
              left: spot.left,
              width: spot.width,
              height: spot.height,
            }}
          />
          <Dim
            type="button"
            aria-label="Dismiss walkthrough"
            onClick={onSkip}
            style={{
              top: 0,
              left: 0,
              width: '100%',
              height: Math.max(0, spot.top),
            }}
          />
          <Dim
            type="button"
            aria-label="Dismiss walkthrough"
            onClick={onSkip}
            style={{
              top: spot.top,
              left: 0,
              width: Math.max(0, spot.left),
              height: spot.height,
            }}
          />
          <Dim
            type="button"
            aria-label="Dismiss walkthrough"
            onClick={onSkip}
            style={{
              top: spot.top,
              left: spot.left + spot.width,
              width: Math.max(0, vw - (spot.left + spot.width)),
              height: spot.height,
            }}
          />
          <Dim
            type="button"
            aria-label="Dismiss walkthrough"
            onClick={onSkip}
            style={{
              top: spot.top + spot.height,
              left: 0,
              width: '100%',
              height: Math.max(0, vh - (spot.top + spot.height)),
            }}
          />
          <Card
            ref={cardRef}
            $prefix={tokenPrefix}
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: 6 }}
            transition={{ duration: 0.18 }}
            style={
              cardPos
                ? { top: cardPos.top, left: cardPos.left }
                : { top: TIP_STICKY + TIP_PAD, right: TIP_PAD, left: 'auto' }
            }
          >
            <Eyebrow $prefix={tokenPrefix}>
              {step.eyebrow || 'Walkthrough'}
            </Eyebrow>
            <Title $prefix={tokenPrefix}>{step.title}</Title>
            <Body $prefix={tokenPrefix}>{step.body}</Body>
            <Footer>
              <StepMeta $prefix={tokenPrefix}>
                {stepIndex + 1} / {stepCount}
              </StepMeta>
              <Actions>
                <GhostBtn type="button" $prefix={tokenPrefix} onClick={onSkip}>
                  Skip
                </GhostBtn>
                {onBack && stepIndex > 0 && (
                  <GhostBtn
                    type="button"
                    $prefix={tokenPrefix}
                    onClick={onBack}
                  >
                    Back
                  </GhostBtn>
                )}
                <PrimaryBtn
                  type="button"
                  $prefix={tokenPrefix}
                  onClick={onNext}
                >
                  {isLast ? 'Done' : 'Next'}
                </PrimaryBtn>
              </Actions>
            </Footer>
          </Card>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default Coachmark;
